import { useState, useRef, useEffect, RefObject, CSSProperties, HTMLAttributes } from "react";

// Redux
import { useSelector } from "react-redux";

// React Query
import { useMutation } from "@tanstack/react-query";

// MUI
import { Button, Stack, IconButton, Container } from "@mui/material";

// Third party
import domToImage from "dom-to-image";

// Icons
import CloseCardsIcon from "@/assets/icons/miscellaneous/organizationChart/CloseCards.svg";
import CloseChartIcon from "@/assets/icons/miscellaneous/organizationChart/CloseChart.svg";
import OpenCardsIcon from "@/assets/icons/miscellaneous/organizationChart/OpenCards.svg";
import OpenChartIcon from "@/assets/icons/miscellaneous/organizationChart/OpenChart.svg";
import ZoomInIcon from "@/assets/icons/miscellaneous/organizationChart/ZoomIn.svg";
import ZoomOutIcon from "@/assets/icons/miscellaneous/organizationChart/ZoomOut.svg";

// Components
import ImageButton from "@Layouts/components/Buttons/ImageButton";
import DownloadButton from "@Layouts/components/Buttons/DownloadButton";
import OrgChartNode from "./OrgChartNode";

// Types
import { OrgChartNodeProps } from "@/Miscellaneous/interfaces/orgchart";
import { ILocation } from "@/Common/interfaces/location";

// Styles
import "./style.css";
import clsx from "clsx";

const FIT_CONTENT_CLASSNAME = "fit-content";
const ZOOM_INTENSITY = 0.2;
const MAX_SCALE = 1.2;
const MIN_SCALE = 0.4;

const generateImage = async (el: HTMLElement) => {
	const highResSize = 1440;
	el.style.width = FIT_CONTENT_CLASSNAME;
	const image = await domToImage.toSvg(el, {
		bgcolor: "#fff",
		width: el.offsetWidth < highResSize ? el.offsetWidth + 100 : el.offsetWidth,
		style: {
			transform: "scale(1)",
		},
		filter: (node) => (node as HTMLElement).tagName !== "i",
	});
	el.style.width = "100%";
	const svg = image?.replace("data:image/svg+xml;charset=utf-8,", "");
	const blob = new Blob([svg], { type: "image/svg+xml" });
	return URL.createObjectURL(blob);
};

const viewImage = async (el: HTMLElement) => {
	const image = await generateImage(el);
	window.open(image);
};

const downloadImage = async (el: HTMLElement, location: string) => {
	const image = await generateImage(el);
	const downloadLink = document.createElement("a");
	downloadLink.href = image;
	downloadLink.download = `Organization-Chart - ${location}`;
	downloadLink.click();
};

const handleDrag = () => {
	const div = document.getElementById("scroll") as HTMLDivElement;

	let isDown = false;
	let startX: number;
	let startY: number;
	let scrollLeft: number;
	let scrollTop: number;

	div.addEventListener("mousedown", (e) => {
		isDown = true;
		startX = e.pageX;
		startY = e.pageY;
		scrollLeft = div.scrollLeft;
		scrollTop = div.scrollTop;
		div.classList.add("active");
	});
	div.addEventListener("mousemove", (e) => {
		if (!isDown) {
			return;
		}
		e.preventDefault();
		div.scrollLeft = scrollLeft - (e.pageX - startX) * 2;
		div.scrollTop = scrollTop - (e.pageY - startY) * 2;
	});
	div.addEventListener("mouseleave", () => {
		isDown = false;
		div.classList.remove("active");
	});

	div.addEventListener("mouseup", () => {
		isDown = false;
		div.classList.remove("active");
	});
};

function ZoomHandlers({
	data,
	scale,
	zoomInOutHandler,
}: Readonly<{
	data: OrgChartNodeProps["data"];
	scale: number;
	zoomInOutHandler: (value: string) => void;
}>) {
	return (
		<div>
			<IconButton onClick={() => zoomInOutHandler("zoomIn")} size="small" disabled={data?.loading ?? scale === 1.2}>
				<img
					className={clsx({
						"opacity-40": scale === MAX_SCALE || data?.loading,
						"opacity-100": scale !== MAX_SCALE && !data?.loading,
						"transition-opacity ease-in-out delay-300": true,
					})}
					width={20}
					src={ZoomInIcon}
					alt="Zoom in icon"
				/>
			</IconButton>

			<IconButton
				sx={{
					marginLeft: 2,
				}}
				onClick={() => zoomInOutHandler("zoomOut")}
				size="small"
				disabled={data?.loading ?? scale === 0.4}
			>
				<img
					className={clsx({
						"opacity-50": scale === MIN_SCALE || data?.loading,
						"opacity-100": scale !== MIN_SCALE && !data?.loading,
						"transition-opacity ease-in-out delay-300": true,
					})}
					width={20}
					src={ZoomOutIcon}
					alt="Zoom out icon"
				/>
			</IconButton>
		</div>
	);
}

function ExpansionHandlers({
	data,
	expandAllChart,
	expandAllCards,
	expandAllChartHandler,
	handleExpandAllCards,
}: Readonly<
	OrgChartNodeProps & {
		expandAllChartHandler: () => void;
		handleExpandAllCards: () => void;
	}
>) {
	return (
		<div>
			<Button
				onClick={expandAllChartHandler}
				disabled={data?.loading ? data?.loading : false}
				sx={{
					color: "var(--text-color)",
					textTransform: "none",
					fontFamily: "var(--font-family-v2)",
				}}
				startIcon={
					<img
						className={clsx({
							"opacity-50": data?.loading,
							"opacity-100": !data?.loading,
							"transition-opacity ease-in-out delay-300": true,
						})}
						width={32}
						height={18}
						src={expandAllChart ? OpenChartIcon : CloseChartIcon}
						alt="Expand chart icon"
					/>
				}
			>
				{expandAllChart ? "Expand Chart" : "Collapse Chart"}
			</Button>

			<Button
				onClick={handleExpandAllCards}
				disabled={data?.loading ? data?.loading : false}
				sx={{
					color: "var(--text-color)",
					textTransform: "none",
					fontFamily: "var(--font-family-v2)",
					marginLeft: 1,
				}}
				startIcon={
					<img
						className={clsx({
							"opacity-50": data?.loading,
							"opacity-100": !data?.loading,
							"transition-opacity ease-in-out delay-300": true,
						})}
						width={32}
						height={18}
						src={expandAllCards ? CloseCardsIcon : OpenCardsIcon}
						alt="Expand cards icon"
					/>
				}
			>
				{expandAllCards ? "Close Cards" : "Open Cards"}
			</Button>
		</div>
	);
}

function DownloadHandlers({
	data,
	isPending,
	el,
	viewImageHandler,
	downloadImageHandler,
}: Readonly<{
	data: OrgChartNodeProps["data"];
	isPending: boolean;
	el: RefObject<HTMLLIElement>;
	viewImageHandler: (elem: HTMLLIElement) => void;
	downloadImageHandler: (elem: HTMLLIElement) => void;
}>) {
	const disabled = data?.loading ?? isPending;
	return (
		<div>
			<ImageButton
				disableElevation
				disableRipple
				disableFocusRipple
				disabled={disabled}
				onClick={() => {
					if (el.current) {
						viewImageHandler(el.current);
					}
				}}
				content="View as image"
				size="25"
			/>
			<DownloadButton
				disableElevation
				disableRipple
				disableFocusRipple
				disabled={disabled}
				onClick={() => {
					if (el.current) {
						downloadImageHandler(el.current);
					}
				}}
				content="Download"
				size={25}
				sx={{
					marginLeft: 1,
				}}
			/>
		</div>
	);
}

export default function OrganizationChart({ data }: Readonly<OrgChartNodeProps>) {
	const [expandAllChart, setExpandAllChart] = useState(false);
	const [expandAllCards, setExpandAllCards] = useState(false);
	const [scale, setScale] = useState(1);
	const location = useSelector<{ location: { location: ILocation } }, ILocation>((state) => state.location.location);
	const el = useRef<HTMLLIElement>(null);

	const { mutate: viewImageHandler, isPending } = useMutation({ mutationFn: (elem: HTMLLIElement) => viewImage(elem) });
	const { mutate: downloadImageHandler } = useMutation({ mutationFn: (elem: HTMLLIElement) => downloadImage(elem, location.name) });

	const expandAllChartHandler = () => {
		setExpandAllChart((currentValue) => !currentValue);
	};

	const handleExpandAllCards = () => {
		setExpandAllCards((currentValue) => !currentValue);
	};

	const zoomInOutHandler = (value: string) => {
		switch (value) {
			case "zoomIn":
				if (scale !== MAX_SCALE) {
					setScale((oldValue) => parseFloat((oldValue + ZOOM_INTENSITY).toFixed(1)));
				}
				break;
			case "zoomOut":
				if (scale <= MAX_SCALE && scale > MIN_SCALE) {
					setScale((oldValue) => parseFloat((oldValue - ZOOM_INTENSITY).toFixed(1)));
				}
				break;
			default:
				break;
		}
	};

	useEffect(() => {
		return () => {
			setScale(1);
		};
	}, [location]);

	const scalingParam: Partial<HTMLAttributes<HTMLLIElement>> = {
		style: { "--chart-scale-param": `${scale}` } as CSSProperties,
	};

	return (
		<Container
			sx={{
				position: "relative",
				overflow: "hidden",
				maxHeight: "650px",
			}}
			disableGutters
			maxWidth={false}
			className="content-main content-size"
		>
			<Stack
				sx={{
					position: "absolute",
					top: "0px",
					width: "1440px",
					zIndex: 3,
					backgroundColor: "var(--gray-background)",
					padding: "10px 19px",
				}}
				direction="row"
				alignItems="center"
				justifyContent="space-between"
			>
				<DownloadHandlers
					data={data}
					isPending={isPending}
					el={el}
					viewImageHandler={viewImageHandler}
					downloadImageHandler={downloadImageHandler}
				/>

				<ZoomHandlers data={data} scale={scale} zoomInOutHandler={zoomInOutHandler} />

				<ExpansionHandlers
					data={data}
					expandAllChart={expandAllChart}
					expandAllCards={expandAllCards}
					expandAllChartHandler={expandAllChartHandler}
					handleExpandAllCards={handleExpandAllCards}
				/>
			</Stack>

			<div className="org-chart-container">
				<ul className="org-chart" id="scroll" draggable onDrag={handleDrag}>
					<li
						id="root"
						ref={el}
						className={clsx({
							"root scale-[var(--chart-scale-param)]": true,
							"!w-fit !h-fit": data?.loading ?? scale === 1,
							"!w-full !h-full": !data?.loading && scale !== 1,
						})}
						{...scalingParam}
					>
						<OrgChartNode
							expandAllChart={expandAllChart}
							expandAllCards={expandAllCards}
							onExpandAllCards={handleExpandAllCards}
							data={data}
						/>
					</li>
				</ul>
			</div>
		</Container>
	);
}
